import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, Send, MailCheck, Clock, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { format, parseISO } from "date-fns";
import { safeFormatDate } from "@/lib/utils";

export default function RenewalRemindersPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const load = () =>
    api.get("/renewals/recipients").then((r) => {
      setData(r.data);
      setEmails(r.data.emails || []);
    });

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="p-8" data-testid="renewals-forbidden">
        <PageHeader overline="Administration" title="Renewal Reminders" />
        <div className="px-4 sm:px-6 md:px-10 py-5">
          <Card className="rounded-none border vsm-border p-6 text-sm vsm-muted">
            Admins only.
          </Card>
        </div>
      </div>
    );
  }

  const addEmail = () => {
    const v = draft.trim();
    if (!v) return;
    if (emails.includes(v)) {
      toast.error("Already added");
      return;
    }
    setEmails([...emails, v]);
    setDraft("");
  };

  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/renewals/recipients", { emails });
      toast.success("Recipient list saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm("Run the renewal-reminder job now? This will email all configured recipients for any SIM due in 30/14/7/1 days.")) return;
    setSending(true);
    try {
      const r = await api.post("/renewals/send-now");
      const sent = (r.data.milestones || []).reduce((a, m) => a + (m.sent ? 1 : 0), 0);
      if (r.data.ok) {
        toast.success(`Reminder run complete — ${sent} milestone email(s) sent.`);
      } else {
        toast.error(r.data.error || "Reminder run finished with errors. See last-run summary below.");
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="p-8 vsm-muted text-sm">Loading…</div>;

  return (
    <div data-testid="renewals-page">
      <PageHeader
        overline="Administration"
        title="Renewal Reminders"
        description="Configure who receives the daily SIM renewal email and trigger the job manually."
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        {/* Status strip */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="renewals-status-card">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-x vsm-border">
            <div className="p-5">
              <div className="vsm-overline">Schedule</div>
              <div className="mt-2 flex items-center gap-2 text-sm font-semibold">
                <Clock className="w-4 h-4" /> {data.schedule}
              </div>
              <div className="mt-1 text-xs vsm-muted">
                Milestones: {data.milestones.map((m) => m.label).join(" · ")} before renewal
              </div>
            </div>
            <div className="p-5">
              <div className="vsm-overline">From Address</div>
              <div className="mt-2 text-sm font-mono">{data.from_email || "—"}</div>
              <div className="mt-1 text-xs vsm-muted">{data.from_name}</div>
            </div>
            <div className="p-5">
              <div className="vsm-overline">Last Run</div>
              <div className="mt-2 text-sm font-mono" data-testid="renewals-last-run">
                {data.last_run ? safeFormatDate(data.last_run, "dd MMM yyyy HH:mm 'UTC'") : "—"}
              </div>
              <div className="mt-1 text-xs vsm-muted">
                {data.last_summary?.ok === true && "Successful"}
                {data.last_summary?.ok === false && "With errors"}
                {!data.last_summary && "Not run yet"}
              </div>
            </div>
          </div>
        </Card>

        {/* Recipient editor */}
        <Card className="rounded-none border vsm-border vsm-surface" data-testid="renewals-recipients-card">
          <div className="p-5 border-b vsm-border flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="vsm-overline">Recipients</div>
              <h2 className="text-xl font-bold tracking-tight">Admin email list</h2>
              <p className="text-xs vsm-muted mt-1">
                These addresses receive the daily renewal-reminder email. Each recipient gets the email individually — addresses are not exposed.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={save}
                disabled={saving}
                className="rounded-none vsm-primary-bg hover:opacity-90"
                data-testid="renewals-save-btn"
              >
                {saving ? "Saving…" : "Save Recipients"}
              </Button>
              <Button
                onClick={sendNow}
                disabled={sending || emails.length === 0}
                variant="outline"
                className="rounded-none"
                data-testid="renewals-send-now-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Sending…" : "Send Now"}
              </Button>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div className="flex gap-2">
              <Input
                data-testid="renewals-new-email-input"
                placeholder="name@yourdomain.com"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addEmail())}
                className="rounded-none font-mono text-sm"
              />
              <Button
                onClick={addEmail}
                className="rounded-none"
                variant="outline"
                data-testid="renewals-add-email-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>

            {emails.length === 0 ? (
              <div className="border vsm-border p-6 text-sm vsm-muted text-center">
                <AlertCircle className="w-5 h-5 mx-auto mb-2" />
                No recipients yet. Add one above to start receiving daily renewal reminders.
              </div>
            ) : (
              <ul className="border vsm-border divide-y vsm-border" data-testid="renewals-recipient-list">
                {emails.map((e) => (
                  <li key={e} className="flex items-center justify-between gap-3 px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <MailCheck className="w-4 h-4 vsm-muted" />
                      <span className="font-mono text-sm">{e}</span>
                    </div>
                    <button
                      onClick={() => removeEmail(e)}
                      className="p-1.5 hover:bg-red-100 hover:text-red-700"
                      data-testid={`renewals-remove-${e}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        {/* Last run summary */}
        {data.last_summary && (
          <Card className="rounded-none border vsm-border vsm-surface" data-testid="renewals-last-summary-card">
            <div className="p-5 border-b vsm-border">
              <div className="vsm-overline">Last run</div>
              <h2 className="text-xl font-bold tracking-tight">Milestone results</h2>
            </div>
            <div className="overflow-x-auto vsm-table-scroll vsm-table-scroll vsm-table-scroll">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b vsm-border text-left">
                    <th className="px-4 py-2.5 vsm-overline">Milestone</th>
                    <th className="px-4 py-2.5 vsm-overline">Target Date</th>
                    <th className="px-4 py-2.5 vsm-overline text-right">SIMs</th>
                    <th className="px-4 py-2.5 vsm-overline">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.last_summary.milestones || []).map((m) => (
                    <tr key={m.label} className="border-b vsm-border">
                      <td className="px-4 py-2.5 font-semibold">{m.label}</td>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {m.target_date ? safeFormatDate(m.target_date) : "—"}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono">{m.count}</td>
                      <td className="px-4 py-2.5">
                        {m.sent ? (
                          <span className="inline-flex items-center gap-1 text-green-700">
                            <CheckCircle2 className="w-4 h-4" /> {m.message}
                          </span>
                        ) : (
                          <span className="vsm-muted text-xs">{m.message}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
